import React from 'react';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import Icon, { IconComponent } from './Icon';

export interface ActionListItem {
  icon: IconComponent;
  title: string;
  description?: string;
  onSelect?: () => void;
  /** Shown greyed out without a chevron, e.g. "Return item — available after delivery". */
  disabled?: boolean;
  /** Highlights the icon in the brand colour (e.g. the recommended action). */
  emphasis?: boolean;
  /** A control shown instead of the chevron (e.g. a theme switcher); the row itself isn't clickable. */
  trailing?: React.ReactNode;
}

const RowContent: React.FC<{ item: ActionListItem }> = ({ item }) => (
  <>
    <span
      className={cn(
        'tw-flex tw-h-9 tw-w-9 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-[10px] tw-bg-ds-subtle',
        item.disabled ? 'tw-text-ds-text-tertiary tw-opacity-60' : item.emphasis ? 'tw-text-ds-brand-text' : 'tw-text-ds-text-secondary',
      )}
    >
      <Icon icon={item.icon} />
    </span>
    <span className={cn('tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-0.5', item.disabled && 'tw-opacity-60')}>
      <span className={cn('tw-text-[15px] tw-font-semibold tw-leading-5', item.disabled ? 'tw-text-ds-text-tertiary' : 'tw-text-ds-text-primary')}>
        {item.title}
      </span>
      {item.description && <span className="tw-text-[13px] tw-leading-[18px] tw-text-ds-text-secondary">{item.description}</span>}
    </span>
  </>
);

/** Card of tappable rows: icon tile, title, description and chevron (Figma "Settings row" / order "Actions"). */
const ActionList: React.FC<{ items: ActionListItem[]; className?: string }> = ({ items, className }) => (
  <ul className={cn('tw-flex tw-flex-col tw-divide-y tw-divide-ds-border-default tw-rounded-2xl tw-border tw-border-ds-border-default tw-bg-ds-surface tw-px-5 tw-py-1', className)}>
    {items.map((item) => (
      <li key={item.title}>
        {item.trailing ? (
          <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-[14px] tw-gap-y-3 tw-py-[14px]">
            <div className="tw-flex tw-min-w-[200px] tw-flex-1 tw-items-center tw-gap-[14px]">
              <RowContent item={item} />
            </div>
            {item.trailing}
          </div>
        ) : (
          <button
            type="button"
            onClick={item.onSelect}
            disabled={item.disabled || !item.onSelect}
            className="tw-group tw-flex tw-w-full tw-items-center tw-gap-[14px] tw-rounded-lg tw-py-[14px] tw-text-left focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-ds-brand disabled:tw-cursor-default"
          >
            <RowContent item={item} />
            {!item.disabled && item.onSelect && (
              <Icon icon={ChevronRight} className="tw-text-ds-text-tertiary tw-transition-transform group-hover:tw-translate-x-0.5" />
            )}
          </button>
        )}
      </li>
    ))}
  </ul>
);

export default ActionList;
